import './RichEditor.css';

import EditorJS from '@editorjs/editorjs';
import type { ToolConstructable } from '@editorjs/editorjs';
import Header from '@editorjs/header';
import NestedList from '@editorjs/nested-list';
import { Box, Input } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useController } from 'react-hook-form';
import type { FieldValues, UseControllerProps } from 'react-hook-form';

import { parseError } from '../../reactHookFormMantine/lib/parseError.ts';

type RichEditorProps<T extends FieldValues> = UseControllerProps<T> & {
  label: string;
  placeholder?: string;
};

export function RichEditor<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  label,
  placeholder,
  ...props
}: RichEditorProps<T>) {
  const editorInstance = useRef<EditorJS | null>(null);

  const {
    field: { value, onChange },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  useEffect(() => {
    if (editorInstance.current) {
      return;
    }

    editorInstance.current = new EditorJS({
      holder: 'editorjs',
      placeholder: placeholder ?? 'Tell us more about your event',
      data: value,
      minHeight: 0,
      tools: {
        paragraph: { config: { preserveBlank: true } },
        // Both plugins ship their own types, which do not structurally match the
        // ToolConstructable of the editorjs version in use: Header's constructor takes a
        // required config and NestedList's conversionConfig.import returns ListData instead
        // of string. Only the declarations disagree, the runtime contract is fine.
        header: { class: Header as unknown as ToolConstructable, config: { levels: [3, 4], defaultLevel: 4 } },
        nestedlist: {
          class: NestedList as unknown as ToolConstructable,
          config: { defaultStyle: 'unordered' },
        },
      },

      onChange: () => {
        const emitContent = async () => {
          const content = await editorInstance.current?.saver.save();

          onChange(content);
        };

        void emitContent();
      },
    });

    return () => {
      editorInstance?.current?.destroy?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error = parseError(label, fieldState.error);

  const border = error ? '1px solid red.5' : '1px solid gray.4';

  return (
    <Input.Wrapper error={error} label={label} required style={{}}>
      <Box
        bd={border}
        bg="white"
        id="editorjs"
        mb="xs"
        px="xxs"
        py="xxxs"
        style={{ borderRadius: 'var(--mantine-radius-xs)' }}
        {...props}
      />
    </Input.Wrapper>
  );
}
